import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { USER_ROLES } from '../../utils/constants';
import { User, LogOut, ChevronDown, Menu, PanelLeftClose, PanelLeft } from 'lucide-react';
import NotificationCenter from '../notifications/NotificationCenter';
import HeaderTickerTape from './HeaderTickerTape';

const Header = ({ onToggleSidebar, sidebarCollapsed, onToggleSidebarCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
    <header
      style={{
        backgroundColor: COLORS.card,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: isMobile ? '0 12px' : '0 24px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* Left Section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
        {/* Mobile Menu Button */}
        <button
          onClick={onToggleSidebar}
          style={{
            background: 'none',
            border: 'none',
            color: COLORS.text,
            cursor: 'pointer',
            padding: '8px',
            display: isMobile ? 'block' : 'none',
          }}
        >
          <Menu size={24} />
        </button>

        {/* Desktop Sidebar Toggle */}
        {!isMobile && (
          <button
            onClick={onToggleSidebarCollapse}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.textSecondary,
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {sidebarCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
          </button>
        )}

        {/* Logo */}
        <Link
          to="/"
          style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: 'bold',
            color: COLORS.primary,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ color: COLORS.primary }}>Ex</span>
          <span style={{ color: COLORS.text }}>Dex</span>
        </Link>

        {/* Ticker Tape - Desktop only */}
        {!isMobile && (
          <div style={{ marginLeft: '24px' }}>
            <HeaderTickerTape />
          </div>
        )}
      </div>

      {/* Right Section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
        {/* Notifications */}
        <NotificationCenter />

        {/* User Menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000',
                fontWeight: 'bold',
              }}
            >
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <ChevronDown size={16} color={COLORS.textSecondary} />
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                minWidth: '200px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                  {user?.email}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '4px' }}>
                  {user?.role?.replace('_', ' ').toUpperCase()}
                </div>
              </div>

              <button
                onClick={() => {
                  navigate('/profile');
                  setShowProfileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: COLORS.text,
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.background)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <User size={16} />
                {t('profile')}
              </button>

              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: COLORS.danger,
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderTop: `1px solid ${COLORS.border}`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.background)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <LogOut size={16} />
                {t('logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
    
    {/* Click outside to close dropdowns */}
    {showProfileMenu && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
        }}
        onClick={() => {
          setShowProfileMenu(false);
        }}
      />
    )}

    </>
  );
};

export default Header;
